import { useCallback, useEffect, useMemo, useState } from 'react';

export function anchoredTooltipPlacement(anchor, dimensions, options = {}) {
  const x = Number(anchor?.x ?? anchor?.centroid?.[0]);
  const y = Number(anchor?.y ?? anchor?.centroid?.[1]);
  const width = Number(dimensions?.width);
  const height = Number(dimensions?.height);
  if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;

  const horizontalClamp = Number(options.horizontalClampPercent ?? 14);
  const topClamp = Number(options.topClampPercent ?? 12);
  const bottomClamp = Number(options.bottomClampPercent ?? 88);
  const flipThreshold = Number(options.flipThresholdPercent ?? 28);
  const leftPercent = Math.min(100 - horizontalClamp, Math.max(horizontalClamp, (x / width) * 100));
  const topPercent = Math.min(bottomClamp, Math.max(topClamp, (y / height) * 100));

  return {
    leftPercent,
    topPercent,
    placement: topPercent < flipThreshold ? 'bottom' : 'top',
  };
}

export function usePinnedMapTooltip(resetKey) {
  const [hoveredId, setHoveredId] = useState(null);
  const [pinnedId, setPinnedId] = useState(null);

  const clearAll = useCallback(() => {
    setHoveredId(null);
    setPinnedId(null);
  }, []);

  useEffect(() => {
    clearAll();
  }, [clearAll, resetKey]);

  const interaction = useMemo(() => ({
    activeId: hoveredId || pinnedId,
    hoveredId,
    pinnedId,
    isPinned: Boolean(pinnedId && (hoveredId || pinnedId) === pinnedId),
    show: setHoveredId,
    clearHovered() {
      setHoveredId(null);
    },
    hide(id) {
      setHoveredId((current) => (current === id ? null : current));
    },
    togglePinned(id) {
      setPinnedId((current) => (current === id ? null : id));
    },
    clearPinned() {
      setPinnedId(null);
    },
    clearAll,
  }), [clearAll, hoveredId, pinnedId]);

  return interaction;
}
